import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs';

import { PermissionService } from '../auth/permission.service';
import { Perm } from '../auth/security-domains';

/**
 * Route guard that enforces the frontend RBAC model. Reads `data.perm`
 * (a Perm descriptor) or `data.adminOnly`, waits for the PermissionService to
 * resolve the user's role + grants, and redirects denied users to /metrics.
 *
 * Runs AFTER AuthGuard (which only checks authentication).
 */
export const permissionGuard: CanActivateFn = (route) => {
  const perm = inject(PermissionService);
  const router = inject(Router);

  const required: Perm | undefined =
    (route.data?.['perm'] as Perm | undefined)
    ?? (route.data?.['adminOnly'] ? ({ adminOnly: true } as Perm) : undefined);

  if (!required) return true;

  return toObservable(perm.ready).pipe(
    filter((ready) => ready),
    take(1),
    map(() => {
      if (perm.allows(required)) return true;
      router.navigate(['/metrics']);
      return false;
    }),
  );
};
